import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, LifeBuoy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { LanguageToggle } from "@/components/LanguageToggle";
import { useLanguage } from "@/context/LanguageContext";
import { useHelp } from "@/components/HelpWidget";
import { cn } from "@/lib/utils";

const navItems = [
  { to: "/", key: "nav_home", testid: "nav-home-link" },
  { to: "/updates", key: "nav_updates", testid: "nav-updates-link" },
  { to: "/supreme-court-lawyers", key: "nav_lawyers", testid: "nav-lawyers-link" },
  { to: "/advice", key: "nav_advice", testid: "nav-advice-link" },
  { to: "/resources", key: "nav_junior_hub", testid: "nav-resources-link" },
  { to: "/insights", key: "nav_insights", testid: "nav-insights-link" },
  { to: "/team", key: "nav_team", testid: "nav-team-link" },
  { to: "/about", key: "nav_about", testid: "nav-about-link" },
];

export const Header = () => {
  const { t } = useLanguage();
  const { open } = useHelp();
  const [mobileOpen, setMobileOpen] = useState(false);

  const linkClass = ({ isActive }) =>
    cn("relative text-sm font-medium transition-colors hover:text-primary", isActive ? "text-primary" : "text-foreground/80");

  return (
    <header className="sticky top-0 z-40 border-b bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70">
      <div className="mx-auto flex h-16 w-full max-w-6xl items-center justify-between px-4 sm:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2" data-testid="brand-logo-link">
          <img
            src="/li-logo.svg"
            alt="Litigation India — Insight First, Action Next"
            className="h-9 w-auto object-contain sm:h-10"
            fetchPriority="high"
            decoding="sync"
          />
        </Link>

        <nav className="hidden items-center gap-7 md:flex">
          {navItems.map((item) => (
            <NavLink key={item.to} to={item.to} className={linkClass} data-testid={item.testid} end={item.to === "/"}>
              {t(item.key)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <LanguageToggle className="hidden sm:inline-flex" />
          <Button onClick={open} className="hidden sm:inline-flex grad-saffron text-white hover:opacity-90" data-testid="header-get-help-button">
            <LifeBuoy className="mr-1 h-4 w-4" /> {t("get_help")}
          </Button>

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" className="md:hidden" data-testid="mobile-menu-button" aria-label="Open menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <div className="mt-8 flex flex-col gap-1">
                {navItems.map((item) => (
                  <SheetClose asChild key={item.to}>
                    <NavLink to={item.to} className="rounded-md px-3 py-3 text-base font-medium hover:bg-secondary" end={item.to === "/"}>
                      {t(item.key)}
                    </NavLink>
                  </SheetClose>
                ))}
                <div className="mt-4 flex items-center justify-between px-3"><LanguageToggle /></div>
                <Button onClick={() => { setMobileOpen(false); open(); }} className="mt-4 grad-saffron text-white" data-testid="mobile-get-help-button">
                  <LifeBuoy className="mr-1 h-4 w-4" /> {t("get_help")}
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
